<template>
  <div class="proxy-checker-section my-4">
    <div class="text-secondary mb-2">
      <p>Enter proxy list (one proxy per line, supports ip:port, ip:port:user:pass):</p>
    </div>
    <div class="row">
      <div class="col-12 mb-3">
        <div class="card jn-card" :class="{ 'dark-mode dark-mode-border': isDarkMode }">
          <div class="card-body">
            <textarea class="form-control mb-2" :class="{ 'dark-mode': isDarkMode }" rows="6"
              v-model="proxyList" :disabled="isLoading" placeholder="1.2.3.4:8080\n1.2.3.4:8080:user:pass\n..." />
            <div class="d-flex align-items-center mb-2">
              <button class="btn btn-primary me-2" @click="onSubmit" :disabled="isLoading || !proxyList.trim()">
                <span v-if="!isLoading">Check Proxy</span>
                <span v-else class="spinner-grow spinner-grow-sm" aria-hidden="true"></span>
              </button>
              <button class="btn btn-outline-secondary" @click="clearAll" :disabled="isLoading">Clear All</button>
            </div>
            <div v-if="errorMsg" class="alert alert-danger py-2">{{ errorMsg }}</div>
            <div v-if="results.length" class="mt-3">
              <h5>Check Results:</h5>
              <div class="table-responsive">
                <table class="table table-bordered table-sm align-middle" :class="{ 'table-dark': isDarkMode }">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Proxy</th>
                      <th>Status</th>
                      <th>Type</th>
                                            <th>Speed (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in results" :key="idx">
                      <td>{{ idx + 1 }}</td>
                      <td><code>{{ item.proxy }}</code></td>
                      <td>
                        <span v-if="item.success" class="text-success">Live</span>
                        <span v-else class="text-danger">Die</span>
                      </td>
                      <td>{{ item.type || '-' }}</td>
                      <td>{{ item.latency || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button class="btn btn-outline-primary btn-sm mt-2" @click="copyResults">Copy Results</button>
              <span v-if="copied" class="text-success ms-2">Copied!</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useMainStore } from '@/store';
const store = useMainStore();
const isDarkMode = computed(() => store.isDarkMode);

const proxyList = ref('');
const results = ref([]);
const isLoading = ref(false);
const errorMsg = ref('');
const copied = ref(false);

const onSubmit = async () => {
  errorMsg.value = '';
  results.value = [];
  copied.value = false;
  if (!proxyList.value.trim()) return;
  isLoading.value = true;
  try {
    const res = await fetch('/api/proxy-checker', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proxies: proxyList.value.split('\n').map(x => x.trim()).filter(Boolean) })
    });
    if (!res.ok) throw new Error('Network or server error');
    const data = await res.json();
    if (Array.isArray(data)) {
      results.value = data;
    } else if (data.results) {
      results.value = data.results;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (e) {
    errorMsg.value = e.message || 'Unknown error';
  } finally {
    isLoading.value = false;
  }
};

const clearAll = () => {
  proxyList.value = '';
  results.value = [];
  errorMsg.value = '';
  copied.value = false;
};

const copyResults = async () => {
  try {
    const text = results.value.map(r => `${r.proxy}\t${r.success ? 'OK' : 'FAIL'}\t${r.type || '-'}\t${r.latency || '-'}\t${r.error || '-'}`).join('\n');
    await navigator.clipboard.writeText(text);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  } catch {}
};
</script>

<style scoped>
.proxy-checker-section textarea {
  font-family: 'Fira Mono', 'Consolas', 'monospace';
}
</style> 